// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Squarebit Eye product pages / widget (store front end). The Eye software
// itself is licensed separately under the Squarebit Eye End User License
// Agreement: https://store.squarebitstudios.com/squarebit-eye/eula
// ==========================================================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import DocsLayout, {
  NavSection
} from '../../common/components/docs-layout.component';

// Sidebar labels are keyed under eyeDocs.layout.*. Where a sub-item names the
// exact same group as the live widget on the product page (Iris & pupil,
// Limbus, Cornea & refraction, ...), we reuse the widget's own squarebitEye.*
// key so the docs TOC and the widget can never drift apart in any language.
// The anchor ids are load-bearing (cross-page links point at them) — never
// translate or rename them.
const EyeDocsLayout: React.FC = () => {
  const { t } = useTranslation();

  const NAV_SECTIONS: NavSection[] = [
    {
      title: t('eyeDocs.layout.sectionTitle'),
      items: [
        { label: t('eyeDocs.layout.overview'), to: '/squarebit-eye/docs' },
        // Installation FIRST: you cannot set an eye up in a host you have not
        // installed it into, so the reading order follows the doing order.
        {
          label: t('eyeDocs.layout.installation'),
          to: '/squarebit-eye/docs/installation',
          subItems: [
            { label: t('eyeDocs.layout.mayaArnold'), anchor: 'maya' },
            { label: t('eyeDocs.layout.blender'), anchor: 'blender' },
            { label: t('eyeDocs.layout.unreal'), anchor: 'unreal' },
            { label: t('eyeDocs.layout.web'), anchor: 'web' },
            { label: t('eyeDocs.layout.usd'), anchor: 'usd' },
            { label: t('eyeDocs.layout.houdini'), anchor: 'houdini' },
            { label: t('eyeDocs.layout.omniverse'), anchor: 'omniverse' },
            { label: t('eyeDocs.layout.materialx'), anchor: 'materialx' }
          ]
        },
        {
          label: t('eyeDocs.layout.gettingStarted'),
          to: '/squarebit-eye/docs/getting-started',
          subItems: [
            { label: t('eyeDocs.layout.mayaArnold'), anchor: 'maya' },
            { label: t('eyeDocs.layout.blender'), anchor: 'blender' },
            { label: t('eyeDocs.layout.unreal'), anchor: 'unreal' },
            { label: t('eyeDocs.layout.usd'), anchor: 'usd' },
            { label: t('eyeDocs.layout.houdini'), anchor: 'houdini' },
            { label: t('eyeDocs.layout.omniverse'), anchor: 'omniverse' },
            { label: t('eyeDocs.gettingStarted.renderersTitle'), anchor: 'renderers' },
            { label: t('eyeDocs.layout.web'), anchor: 'web' }
          ]
        },
        {
          label: t('eyeDocs.layout.looks'),
          to: '/squarebit-eye/docs/sharing-looks',
          subItems: [
            { label: t('eyeDocs.looks.formatsTitle'), anchor: 'formats' },
            { label: t('eyeDocs.layout.mayaArnold'), anchor: 'maya' },
            { label: t('eyeDocs.layout.blender'), anchor: 'blender' },
            { label: t('eyeDocs.layout.unreal'), anchor: 'unreal' },
            { label: t('eyeDocs.layout.web'), anchor: 'web' }
          ]
        },
        {
          label: t('eyeDocs.layout.parameters'),
          to: '/squarebit-eye/docs/parameters',
          subItems: [
            { label: t('squarebitEye.sectionIris'), anchor: 'iris-pupil' },
            {
              label: t('eyeDocs.layout.proceduralIris'),
              anchor: 'procedural-iris'
            },
            { label: t('squarebitEye.sectionLimbus'), anchor: 'limbus' },
            { label: t('squarebitEye.sectionCornea'), anchor: 'cornea' },
            { label: t('squarebitEye.sectionHighlight'), anchor: 'highlight' },
            { label: t('squarebitEye.sectionLight'), anchor: 'light' }
          ]
        },
        {
          label: t('eyeDocs.layout.textures'),
          to: '/squarebit-eye/docs/textures',
          subItems: [
            { label: t('eyeDocs.layout.irisMap'), anchor: 'iris-map' },
            { label: t('eyeDocs.layout.scleraMap'), anchor: 'sclera-map' },
            { label: t('eyeDocs.layout.limbusMap'), anchor: 'limbus-map' },
            { label: t('eyeDocs.layout.surfaceMap'), anchor: 'surface-map' },
            { label: t('eyeDocs.layout.baking'), anchor: 'baking' }
          ]
        },
        {
          label: t('eyeDocs.layout.rigging'),
          to: '/squarebit-eye/docs/rigging',
          subItems: [
            { label: t('eyeDocs.layout.projector'), anchor: 'projector' },
            { label: t('eyeDocs.layout.channels'), anchor: 'channels' },
            { label: t('eyeDocs.layout.mayaRig'), anchor: 'maya-rig' },
            { label: t('eyeDocs.layout.unrealRig'), anchor: 'unreal-rig' },
            { label: t('eyeDocs.layout.lidConform'), anchor: 'lid-conform' },
            { label: t('eyeDocs.layout.blenderRig'), anchor: 'blender-rig' },
            { label: t('eyeDocs.layout.webRig'), anchor: 'web-rig' },
            { label: t('eyeDocs.layout.usdRig'), anchor: 'usd-rig' }
          ]
        }
      ]
    }
  ];

  return <DocsLayout navSections={NAV_SECTIONS} />;
};

export default EyeDocsLayout;
